// Reservas del hotel: listado filtrable + «Nueva reserva» en página completa
// (features/hotel/NuevaReserva.tsx, sin ventana modal) y acceso al detalle
// (features/hotel/ReservaDetalle.tsx) con clic en la fila.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Campo, fieldCls, money, useSort, SortTh,
  hotelRpc, QK, hoyISO, sumarDias, fmtFecha, ESTADO_RESERVA,
  type EstadoReserva, type ReservaFila,
} from './shared';
import { ReservaDetalle } from './ReservaDetalle';
import { NuevaReserva } from './NuevaReserva';

// Debounce simple para el campo de búsqueda (sin disparar un POST por tecla).
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function ReservasHotel() {
  const [desde, setDesde] = useState(hoyISO());
  const [hasta, setHasta] = useState(sumarDias(hoyISO(), 30));
  const [estado, setEstado] = useState<'' | EstadoReserva>('');
  const [buscaInput, setBuscaInput] = useState('');
  const busca = useDebounced(buscaInput);
  const [abrirNueva, setAbrirNueva] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);

  const { data: filas = [], isLoading } = useQuery<ReservaFila[]>({
    queryKey: [QK, 'reservas', desde, hasta, estado, busca],
    queryFn: () => hotelRpc<ReservaFila[]>('reservas_listar', { _desde: desde, _hasta: hasta, _estado: estado || null, _busca: busca.trim() || null }),
  });

  const getVal = (r: ReservaFila, key: string): string | number | null => {
    switch (key) {
      case 'codigo': return r.codigo;
      case 'huesped': return r.huesped;
      case 'tipo': return `${r.tipo}${r.habitacion ? ` ${r.habitacion}` : ''}`;
      case 'llegada': return r.llegada;
      case 'salida': return r.salida;
      case 'noches': return r.noches;
      case 'estado': return ESTADO_RESERVA[r.estado]?.label ?? r.estado;
      case 'total': return Number(r.total);
      case 'deposito': return Number(r.deposito_pagado);
      default: return null;
    }
  };
  const sort = useSort(filas, getVal);

  const totales = useMemo(() => filas.reduce((a, r) => ({
    noches: a.noches + Number(r.noches || 0),
    total: a.total + Number(r.total || 0),
    reqd: a.reqd + Number(r.deposito_requerido || 0),
    pag: a.pag + Number(r.deposito_pagado || 0),
  }), { noches: 0, total: 0, reqd: 0, pag: 0 }), [filas]);

  if (abrirNueva) {
    return <NuevaReserva onClose={() => setAbrirNueva(false)} onCreated={(id) => { setAbrirNueva(false); setDetalleId(id); }} />;
  }

  if (detalleId) {
    return <ReservaDetalle reservaId={detalleId} onClose={() => setDetalleId(null)} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label="Estado">
            <select className={fieldCls} value={estado} onChange={(e) => setEstado(e.target.value as '' | EstadoReserva)}>
              <option value="">Todos</option>
              {(Object.keys(ESTADO_RESERVA) as EstadoReserva[]).map((k) => <option key={k} value={k}>{ESTADO_RESERVA[k].label}</option>)}
            </select>
          </Campo>
          <Campo label="Buscar (código, huésped, identificación)">
            <input className={`${fieldCls} w-64`} value={buscaInput} onChange={(e) => setBuscaInput(e.target.value)} placeholder="Buscar…" />
          </Campo>
        </div>
        <Button onClick={() => setAbrirNueva(true)}><Plus className="mr-1 h-4 w-4" /> Nueva reserva</Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-3">N°</th>
              <SortTh col="codigo" sort={sort}>Código</SortTh>
              <SortTh col="huesped" sort={sort}>Huésped</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo / Habitación</SortTh>
              <SortTh col="llegada" sort={sort}>Llegada</SortTh>
              <SortTh col="salida" sort={sort}>Salida</SortTh>
              <SortTh col="noches" sort={sort} align="right">Noches</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <SortTh col="total" sort={sort} align="right">Total</SortTh>
              <SortTh col="deposito" sort={sort} align="right">Depósito</SortTh>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="py-10 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && sort.sorted.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-muted-foreground">Sin reservas en el rango seleccionado.</td></tr>}
              {sort.sorted.map((r, i) => {
                const est = ESTADO_RESERVA[r.estado];
                const okDeposito = r.deposito_pagado >= r.deposito_requerido;
                return (
                  <tr key={r.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/50" onClick={() => setDetalleId(r.id)}>
                    <td className="py-2 pl-3 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                    <td className="py-2 pr-3">{r.huesped ?? '—'}</td>
                    <td className="py-2 pr-3">{r.tipo}{r.habitacion ? <span className="text-xs text-muted-foreground"> · Hab. {r.habitacion}</span> : <span className="text-xs text-muted-foreground"> · sin asignar</span>}</td>
                    <td className="py-2 pr-3">{fmtFecha(r.llegada)}</td>
                    <td className="py-2 pr-3">{fmtFecha(r.salida)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.noches}</td>
                    <td className="py-2 pr-3"><span className={`rounded px-2 py-0.5 text-xs font-medium ${est?.cls ?? ''}`}>{est?.label ?? r.estado}</span></td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.total)}</td>
                    <td className={`py-2 pr-3 text-right font-mono tabular-nums ${okDeposito ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {money(r.deposito_pagado)} / {money(r.deposito_requerido)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filas.length > 0 && (
              <tfoot>
                <tr className="border-t-2 bg-muted/30 font-semibold">
                  <td className="py-2 pl-3 pr-3" colSpan={6}>Totales ({filas.length})</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{totales.noches}</td>
                  <td className="py-2 pr-3"></td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totales.total)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totales.pag)} / {money(totales.reqd)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

    </div>
  );
}
